import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Mail, Phone, Building2, Tag, Coins, Edit3, Trash2, EyeOff, Plus, Briefcase, Calendar, AlertCircle, Save, ListTodo, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { acheteur: 'Acheteur', locataire: 'Locataire', investisseur: 'Investisseur', partenaire: 'Partenaire', architecte: 'Architecte', autre: 'Autre' };
const STAGE_COLORS = {
  prospect:   '#A0A0A0',
  visite:     '#F59E0B',
  offre:      '#A855F7',
  compromis:  '#3B82F6',
  acte:       '#10B981',
  abandonne:  '#EF4444',
};
const ACTIVITY_TYPES = [
  { value: 'note',   label: 'Note' },
  { value: 'appel',  label: 'Appel' },
  { value: 'email',  label: 'Email' },
  { value: 'rdv',    label: 'RDV' },
  { value: 'visite', label: 'Visite' },
  { value: 'tache',  label: 'Tâche' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const CrmContactDrawer = ({ contactId, currentUserId, onClose, onChange, onEdit }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('infos');
  const [busy, setBusy] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showOpp, setShowOpp] = useState(false);
  const [linkingProject, setLinkingProject] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [actForm, setActForm] = useState({ type: 'note', title: '', description: '', happened_at: new Date().toISOString().slice(0, 16) });
  const [oppForm, setOppForm] = useState({
    title: '',
    stage: 'prospect',
    amount_eur: '',
    probability: 50,
    project_id: '',
    tags: [],
  });
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [suggestSnippet, setSuggestSnippet] = useState('');
  const [suggesting, setSuggesting] = useState(false);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/crm/contacts/${contactId}`, { withCredentials: true });
      setData(r.data);
    } catch {
      toast.error('Erreur chargement contact');
      onClose();
    }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [contactId]);

  // Charge la liste des projets pour le sélecteur de liaison
  useEffect(() => {
    axios.get(`${API}/projects`, { withCredentials: true })
      .then(r => setAllProjects(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setAllProjects([]));
  }, []);

  const linkProject = async (projectId) => {
    try {
      await axios.post(`${API}/crm/contacts/${contactId}/link-project`, { project_id: projectId }, { withCredentials: true });
      toast.success('Projet lié au contact');
      setLinkingProject(false);
      fetchData();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur liaison');
    }
  };

  const unlinkProject = async (projectId) => {
    try {
      await axios.delete(`${API}/crm/contacts/${contactId}/link-project/${projectId}`, { withCredentials: true });
      toast.message('Liaison retirée');
      fetchData();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (!data) return null;
  const c = data.contact;
  const isOwner = c.owner_user_id === currentUserId;
  const linkedProjectIds = c.linked_project_ids || [];
  const linkedProjects = allProjects.filter(p => linkedProjectIds.includes(p.project_id));
  const availableProjects = allProjects.filter(p => !linkedProjectIds.includes(p.project_id));

  const handleArchive = async () => {
    if (!window.confirm('Archiver ce contact ? Il sera marqué comme inactif.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/crm/contacts/${contactId}`, { withCredentials: true });
      toast.success('Contact archivé');
      onChange?.();
      onClose();
    } catch { toast.error('Erreur'); } finally { setBusy(false); }
  };

  const handleAnonymize = async () => {
    if (!window.confirm('⚠️ ANONYMISATION RGPD\n\nCette action est IRRÉVERSIBLE et conforme au droit à l\'effacement (RGPD Art. 17).\n\nLes données personnelles seront effacées définitivement (nom, email, téléphone, société, notes), mais l\'historique des opportunités sera préservé pour l\'intégrité statistique.\n\nConfirmer ?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/crm/contacts/${contactId}/anonymize`, {}, { withCredentials: true });
      toast.success('Contact anonymisé conformément RGPD');
      onChange?.();
      onClose();
    } catch { toast.error('Erreur anonymisation'); } finally { setBusy(false); }
  };

  const handleAddActivity = async () => {
    if (!actForm.title.trim()) return toast.error('Titre requis');
    try {
      await axios.post(`${API}/crm/contacts/${contactId}/activities`, {
        ...actForm,
        happened_at: new Date(actForm.happened_at).toISOString(),
      }, { withCredentials: true });
      toast.success('Activité enregistrée');
      setShowActivity(false);
      setActForm({ type: 'note', title: '', description: '', happened_at: new Date().toISOString().slice(0, 16) });
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleAddOpp = async () => {
    if (!oppForm.title.trim()) return toast.error('Titre requis');
    try {
      await axios.post(`${API}/crm/opportunities`, {
        contact_id: contactId,
        title: oppForm.title,
        stage: oppForm.stage,
        amount_eur: oppForm.amount_eur ? parseFloat(oppForm.amount_eur) : null,
        probability: parseInt(oppForm.probability, 10),
        project_id: oppForm.project_id || null,
        tags: oppForm.tags && oppForm.tags.length ? oppForm.tags : null,
      }, { withCredentials: true });
      toast.success('Opportunité créée');
      setShowOpp(false);
      setOppForm({ title: '', stage: 'prospect', amount_eur: '', probability: 50, project_id: '', tags: [] });
      setSuggestedTags([]);
      setSuggestSnippet('');
      fetchData();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  // Auto-generate title + tags from a project's structured data (Sprint AP1
  // surface/typology/outdoor/orientation + address). One-click filler.
  const handleSuggestOpp = async () => {
    if (!oppForm.project_id) {
      toast.error('Sélectionnez d\'abord un projet lié');
      return;
    }
    setSuggesting(true);
    try {
      const r = await axios.get(
        `${API}/crm/opportunities/suggest?project_id=${oppForm.project_id}`,
        { withCredentials: true },
      );
      setOppForm((f) => ({
        ...f,
        title: r.data.title || f.title,
        tags: r.data.tags || [],
      }));
      setSuggestedTags(r.data.tags || []);
      setSuggestSnippet(r.data.snippet || '');
      toast.success('Titre & tags pré-remplis ✨');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggestion indisponible');
    } finally { setSuggesting(false); }
  };

  const toggleTag = (t) => {
    setOppForm((f) => {
      const has = (f.tags || []).includes(t);
      return { ...f, tags: has ? f.tags.filter((x) => x !== t) : [...(f.tags || []), t] };
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1400] animate-in fade-in duration-200" onClick={onClose} data-testid="crm-drawer-backdrop" />
      <div
        className="fixed top-0 right-0 bottom-0 w-full max-w-[480px] bg-gradient-to-b from-[#0F0F12] to-[#09090B] border-l border-white/10 shadow-2xl z-[1410] flex flex-col animate-in slide-in-from-right duration-300"
        data-testid="crm-contact-drawer"
      >
        <div className="px-5 pt-5 pb-3 border-b border-white/10 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D9A05B] font-bold">▸ {ROLE_LABELS[c.role] || c.role}</p>
            <h2 className="text-2xl font-semibold text-white truncate mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {c.first_name}{c.last_name ? ` ${c.last_name}` : ''}
            </h2>
            {c.company && <p className="text-xs text-white/55 mt-1 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{c.company}</p>}
            {!isOwner && <p className="text-[10px] uppercase tracking-widest text-white/35 flex items-center gap-1 mt-2"><EyeOff className="w-3 h-3" /> Lecture seule (autre membre)</p>}
            {c.anonymized && <p className="text-[10px] uppercase tracking-widest text-amber-400/80 flex items-center gap-1 mt-2"><AlertCircle className="w-3 h-3" /> Contact anonymisé RGPD</p>}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="crm-drawer-close" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b border-white/10 shrink-0">
          {[
            { id: 'infos', label: 'Infos' },
            { id: 'opps', label: `Opportunités (${data.opportunities?.length || 0})` },
            { id: 'activities', label: `Activités (${data.activities?.length || 0})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`crm-drawer-tab-${t.id}`}
              className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 ${tab === t.id ? 'text-[#E8B26C] border-[#D9A05B]' : 'text-white/45 hover:text-white/80 border-transparent'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'infos' && (
            <div className="space-y-3 text-sm">
              {/* Projets liés */}
              <div
                className="rounded-lg p-3"
                style={{ background: 'rgba(217,160,91,0.06)', border: '1px solid var(--c-border-accent)' }}
                data-testid="crm-linked-projects-block"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="hud-eyebrow hud-eyebrow-gold">Projets liés</span>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => setLinkingProject(v => !v)}
                      className="hud-btn hud-btn-ghost hud-btn-sm"
                      data-testid="crm-link-project-btn"
                    >
                      <Plus className="w-3 h-3" />
                      Lier
                    </button>
                  )}
                </div>
                {linkedProjects.length === 0 && !linkingProject && (
                  <p className="text-[12px]" style={{ color: 'var(--c-text-muted)' }}>
                    Ce contact n'est lié à aucun projet.
                  </p>
                )}
                {linkedProjects.length > 0 && (
                  <div className="space-y-1.5">
                    {linkedProjects.map(p => (
                      <div
                        key={p.project_id}
                        className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md"
                        style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
                        data-testid={`crm-linked-project-${p.project_id}`}
                      >
                        <span className="text-[12.5px] truncate" style={{ color: 'var(--c-text)' }}>
                          {p.name}
                        </span>
                        {isOwner && (
                          <button
                            onClick={() => unlinkProject(p.project_id)}
                            className="text-[10px] uppercase tracking-widest"
                            style={{ color: 'var(--c-text-muted)' }}
                            data-testid={`crm-unlink-project-${p.project_id}`}
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {linkingProject && availableProjects.length > 0 && (
                  <div className="mt-2 max-h-44 overflow-y-auto space-y-1 pr-1">
                    {availableProjects.map(p => (
                      <button
                        key={p.project_id}
                        onClick={() => linkProject(p.project_id)}
                        className="w-full text-left px-2.5 py-1.5 rounded-md transition-colors text-[12.5px]"
                        style={{ background: 'var(--c-surface-1)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface-3)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--c-surface-1)'; }}
                        data-testid={`crm-link-pick-${p.project_id}`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                )}
                {linkingProject && availableProjects.length === 0 && (
                  <p className="text-[11px] mt-1.5" style={{ color: 'var(--c-text-muted)' }}>
                    Aucun projet disponible (tous déjà liés).
                  </p>
                )}
              </div>

              {c.email && <p className="flex items-center gap-2 text-white/85"><Mail className="w-3.5 h-3.5 text-white/40" /> {c.email}</p>}
              {c.phone && <p className="flex items-center gap-2 text-white/85"><Phone className="w-3.5 h-3.5 text-white/40" /> {c.phone}</p>}
              {c.budget_min_eur && (
                <p className="flex items-center gap-2 text-[#D9A05B]"><Coins className="w-3.5 h-3.5" /> Budget : {c.budget_min_eur.toLocaleString('fr-FR')}€{c.budget_max_eur ? ` – ${c.budget_max_eur.toLocaleString('fr-FR')}€` : ''}</p>
              )}
              {c.surface_min_m2 && (
                <p className="flex items-center gap-2 text-white/85"><Building2 className="w-3.5 h-3.5 text-white/40" /> Surface : {c.surface_min_m2} – {c.surface_max_m2 || '∞'} m²</p>
              )}
              {c.desired_typology && <p className="text-white/75"><span className="text-white/40">Typologie :</span> {c.desired_typology}</p>}
              {(c.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {c.tags.map(t => <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-white/70 border border-white/10"># {t}</span>)}
                </div>
              )}
              {c.notes && (
                <div className="mt-3 p-3 bg-white/[0.03] border border-white/10 rounded-lg whitespace-pre-wrap text-white/75 text-xs leading-relaxed">{c.notes}</div>
              )}
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 mt-4 text-[11px] text-white/50">
                <div>
                  <p className="text-white/35 uppercase tracking-widest text-[9px] font-bold mb-0.5">Créé</p>
                  {fmtDate(c.created_at)}
                </div>
                <div>
                  <p className="text-white/35 uppercase tracking-widest text-[9px] font-bold mb-0.5">Mis à jour</p>
                  {fmtDate(c.updated_at)}
                </div>
                <div>
                  <p className="text-white/35 uppercase tracking-widest text-[9px] font-bold mb-0.5">RGPD Marketing</p>
                  {c.consent_marketing ? <span className="text-emerald-400">✓ Opt-in {fmtDate(c.consent_marketing_at)}</span> : <span className="text-white/40">Non opt-in</span>}
                </div>
                <div>
                  <p className="text-white/35 uppercase tracking-widest text-[9px] font-bold mb-0.5">RGPD Données</p>
                  {c.consent_data_processing ? <span className="text-emerald-400">✓ Consenti</span> : <span className="text-white/40">Non consenti</span>}
                </div>
              </div>
            </div>
          )}

          {tab === 'opps' && (
            <div>
              {isOwner && !showOpp && (
                <Button onClick={() => setShowOpp(true)} variant="outline" className="w-full mb-3 bg-white/5 border-white/15 text-white hover:bg-white/10" data-testid="crm-add-opp-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle opportunité
                </Button>
              )}
              {showOpp && (
                <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 mb-3 space-y-2">
                  {/* Project selector — drives the Suggest IA button. We prioritize linked projects then fall back to all. */}
                  {(() => {
                    const linked = data.linked_projects || [];
                    const projectOptions = linked.length > 0 ? linked : allProjects;
                    return (
                      <div className="flex gap-2">
                        <select
                          value={oppForm.project_id}
                          onChange={e => setOppForm({ ...oppForm, project_id: e.target.value })}
                          className="bg-white/5 border border-white/15 text-white text-xs rounded h-9 px-2 flex-1 min-w-0"
                          data-testid="crm-opp-project-select"
                        >
                          <option value="" style={{ background: '#1a1a1f' }}>— Projet lié (facultatif) —</option>
                          {projectOptions.map(p => (
                            <option key={p.project_id} value={p.project_id} style={{ background: '#1a1a1f' }}>
                              {p.name}{linked.length === 0 ? ' (non lié)' : ''}
                            </option>
                          ))}
                        </select>
                        <Button
                          onClick={handleSuggestOpp}
                          disabled={!oppForm.project_id || suggesting}
                          variant="outline"
                          className="bg-[#D9A05B]/15 border-[#D9A05B]/40 text-[#D9A05B] hover:bg-[#D9A05B]/25 hover:text-[#D9A05B] text-xs h-9 px-3 shrink-0 disabled:opacity-40"
                          data-testid="crm-opp-suggest-btn"
                        >
                          <Sparkles className="w-3.5 h-3.5 mr-1" />
                          {suggesting ? '…' : 'Suggérer'}
                        </Button>
                      </div>
                    );
                  })()}

                  <Input value={oppForm.title} onChange={e => setOppForm({ ...oppForm, title: e.target.value })} placeholder="Titre — ex. Vente T3 56 m² + balcon — Neuilly" className="text-sm h-9" data-testid="crm-opp-title-input" />

                  {(suggestedTags.length > 0 || (oppForm.tags && oppForm.tags.length > 0)) && (
                    <div data-testid="crm-opp-tags-block">
                      <p className="text-[9.5px] uppercase tracking-widest text-white/45 font-bold mb-1.5 flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" /> Tags
                        {suggestedTags.length > 0 && <span className="text-[#D9A05B]/70 normal-case tracking-normal font-normal">· cliquez pour activer/désactiver</span>}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {/* Active tags first (from oppForm), then unselected suggestions */}
                        {[...new Set([...(oppForm.tags || []), ...suggestedTags])].map((t) => {
                          const active = (oppForm.tags || []).includes(t);
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => toggleTag(t)}
                              data-testid={`crm-opp-tag-${t.replace(/[^a-zA-Z0-9]/g, '-')}`}
                              className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1 rounded-full transition-all"
                              style={{
                                background: active ? 'rgba(217,160,91,0.20)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${active ? 'rgba(217,160,91,0.55)' : 'rgba(255,255,255,0.12)'}`,
                                color: active ? '#FDE68A' : 'rgba(255,255,255,0.50)',
                              }}
                            >
                              {active && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {suggestSnippet && (
                    <p className="text-[10.5px] leading-relaxed px-2 py-1.5 rounded"
                      style={{ background: 'rgba(217,160,91,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(217,160,91,0.18)' }}
                      data-testid="crm-opp-snippet"
                    >
                      <Sparkles className="w-2.5 h-2.5 inline mr-1" style={{ color: '#D9A05B' }} />
                      {suggestSnippet}
                    </p>
                  )}

                  <div className="grid grid-cols-3 gap-2">
                    <select value={oppForm.stage} onChange={e => setOppForm({ ...oppForm, stage: e.target.value })} className="bg-white/5 border border-white/15 text-white text-xs rounded h-9 px-2" data-testid="crm-opp-stage-select">
                      {Object.keys(STAGE_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Input type="number" value={oppForm.amount_eur} onChange={e => setOppForm({ ...oppForm, amount_eur: e.target.value })} placeholder="€ HT" className="text-xs h-9" data-testid="crm-opp-amount-input" />
                    <Input type="number" value={oppForm.probability} onChange={e => setOppForm({ ...oppForm, probability: e.target.value })} placeholder="%" min={0} max={100} className="text-xs h-9" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddOpp} className="bg-[#D9A05B] hover:bg-[#C78D46] text-black text-xs h-8 flex-1" data-testid="crm-opp-save-btn"><Save className="w-3 h-3 mr-1" />Créer</Button>
                    <Button onClick={() => { setShowOpp(false); setSuggestedTags([]); setSuggestSnippet(''); }} variant="ghost" className="text-xs h-8">Annuler</Button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {(data.opportunities || []).length === 0 && <p className="text-center text-white/40 text-xs py-6">Aucune opportunité.</p>}
                {(data.opportunities || []).map(o => (
                  <div key={o.opportunity_id} className="bg-white/[0.03] border border-white/10 rounded-lg p-3" data-testid={`crm-opp-item-${o.opportunity_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{o.title}</p>
                        <p className="text-[11px] text-white/55 mt-0.5">{o.amount_eur ? `${o.amount_eur.toLocaleString('fr-FR')}€` : 'Montant N/A'} · {o.probability}%</p>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" style={{ background: `${STAGE_COLORS[o.stage]}25`, color: STAGE_COLORS[o.stage], border: `1px solid ${STAGE_COLORS[o.stage]}55` }}>
                        {o.stage}
                      </span>
                    </div>
                    {Array.isArray(o.tags) && o.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {o.tags.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="inline-flex text-[9.5px] font-semibold px-1.5 py-0.5 rounded"
                            style={{
                              background: 'rgba(217,160,91,0.10)',
                              color: '#FDE68A',
                              border: '1px solid rgba(217,160,91,0.25)',
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'activities' && (
            <div>
              {isOwner && !showActivity && (
                <Button onClick={() => setShowActivity(true)} variant="outline" className="w-full mb-3 bg-white/5 border-white/15 text-white hover:bg-white/10" data-testid="crm-add-activity-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Logger une activité
                </Button>
              )}
              {showActivity && (
                <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 mb-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <select value={actForm.type} onChange={e => setActForm({ ...actForm, type: e.target.value })} className="bg-white/5 border border-white/15 text-white text-xs rounded h-9 px-2" data-testid="crm-activity-type-select">
                      {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <Input type="datetime-local" value={actForm.happened_at} onChange={e => setActForm({ ...actForm, happened_at: e.target.value })} className="text-xs h-9" />
                  </div>
                  <Input value={actForm.title} onChange={e => setActForm({ ...actForm, title: e.target.value })} placeholder="Titre — ex. Visite samedi matin" className="text-sm h-9" data-testid="crm-activity-title-input" />
                  <textarea value={actForm.description} onChange={e => setActForm({ ...actForm, description: e.target.value })} placeholder="Détails (optionnel)" rows={2} className="w-full bg-white/5 border border-white/15 text-white text-xs rounded p-2 placeholder:text-white/30" />
                  <div className="flex gap-2">
                    <Button onClick={handleAddActivity} className="bg-[#D9A05B] hover:bg-[#C78D46] text-black text-xs h-8 flex-1" data-testid="crm-activity-save-btn"><Save className="w-3 h-3 mr-1" />Enregistrer</Button>
                    <Button onClick={() => setShowActivity(false)} variant="ghost" className="text-xs h-8">Annuler</Button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {(data.activities || []).length === 0 && <p className="text-center text-white/40 text-xs py-6">Aucune activité.</p>}
                {(data.activities || []).map(a => (
                  <div key={a.activity_id} className="bg-white/[0.03] border border-white/10 rounded-lg p-3" data-testid={`crm-activity-row-${a.activity_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm text-white">{a.title}</p>
                      <span className="shrink-0 text-[10px] uppercase tracking-widest font-bold text-[#D9A05B]">{a.type}</span>
                    </div>
                    {a.description && <p className="text-xs text-white/55 mt-1 whitespace-pre-wrap">{a.description}</p>}
                    <p className="text-[10px] text-white/35 mt-1.5 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {fmtDate(a.happened_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {isOwner && !c.anonymized && (
          <div className="border-t border-white/10 px-5 py-3 flex items-center gap-2 shrink-0">
            <Button onClick={() => onEdit?.(c)} variant="outline" className="bg-white/5 border-white/15 text-white hover:bg-white/10 text-xs h-9" data-testid="crm-edit-contact-btn"><Edit3 className="w-3.5 h-3.5 mr-1.5" />Modifier</Button>
            <Button onClick={handleArchive} variant="outline" disabled={busy} className="bg-white/5 border-white/15 text-white hover:bg-white/10 text-xs h-9" data-testid="crm-archive-contact-btn"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Archiver</Button>
            <Button onClick={handleAnonymize} variant="outline" disabled={busy} className="bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20 text-xs h-9 ml-auto" data-testid="crm-anonymize-contact-btn"><EyeOff className="w-3.5 h-3.5 mr-1.5" />RGPD</Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CrmContactDrawer;
